import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PolicySectionEditor } from "@/components/PolicySectionEditor";
import { PolicyContentView } from "@/components/PolicyContentView";
import { SignoffRequests } from "@/components/SignoffRequests";
import { PolicyHistory } from "@/components/PolicyHistory";
import { downloadBlob } from "@/lib/download";
import { ArrowLeft, Download, Loader2, RotateCcw, Save, UserCheck, Trash2, CalendarCheck, BellRing } from "lucide-react";
import { toast } from "sonner";

function MasterUpdateBanner({ doc, sector, title, onChanged }) {
  const [showMaster, setShowMaster] = useState(false);
  const ack = async () => { await api.post(`/me/policies/${sector}/${encodeURIComponent(title)}/acknowledge-update`); toast.success("Marked as reviewed"); onChanged(); };
  const applyMaster = async () => {
    if (!window.confirm("Replace your customised wording with the updated master policy? Your current text is kept as a backup in the audit trail.")) return;
    await api.post(`/me/policies/${sector}/${encodeURIComponent(title)}/apply-master`); toast.success("Updated master applied — ask staff to re-sign"); onChanged();
  };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6 text-sm" data-testid="policy-master-updated-banner">
      <p className="font-medium flex items-center gap-2 text-amber-900"><BellRing className="w-4 h-4" /> The master version of this policy was updated on {new Date(doc.master_updated_at).toLocaleDateString("en-GB")}</p>
      {doc.master_change_note && <p className="mt-1 text-amber-900" data-testid="policy-master-change-note"><b>What changed:</b> {doc.master_change_note}</p>}
      <p className="mt-1 text-amber-800">{doc.customised ? "You keep a customised version, so the change is not applied automatically — compare with the new master and update your text, then ask staff to re-sign." : "Your policy already shows the new wording — review it and ask staff to re-sign."}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {doc.master_content && <Button size="sm" variant="outline" onClick={() => setShowMaster(!showMaster)} data-testid="policy-view-new-master">{showMaster ? "Hide new master" : "View new master"}</Button>}
        {doc.customised && <Button size="sm" onClick={applyMaster} data-testid="policy-apply-master">Apply the updated master</Button>}
        <Button size="sm" variant={doc.customised ? "outline" : "default"} onClick={ack} data-testid="policy-ack-update">I've reviewed this change</Button>
      </div>
      {showMaster && doc.master_content && <div className="mt-4 rounded-lg bg-white border border-border p-4 max-h-96 overflow-y-auto"><PolicyContentView content={doc.master_content} testId="policy-new-master-content" /></div>}
    </div>
  );
}

function VersionHistory({ version, changelog = [] }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 text-sm" data-testid="policy-version-history">
      <p className="font-semibold flex items-center justify-between">Master version <Badge variant="secondary" data-testid="policy-version">v{version || 1}.0</Badge></p>
      {changelog.length === 0 ? <p className="text-xs text-muted-foreground mt-2">Original publication. Regulatory Watch updates this policy automatically when CQC or Ofsted change the rules.</p> : (
        <ul className="mt-2 space-y-2">
          {changelog.map((c) => (
            <li key={c.version} className="text-xs" data-testid={`policy-changelog-${c.version}`}>
              <p className="font-medium">v{c.version}.0 · {new Date(c.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
              <p className="text-muted-foreground">{c.note}</p>
              {c.source_url ? <a href={c.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{c.regulator}: {c.source_title}</a> : <p className="text-muted-foreground">{c.regulator}: {c.source_title}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewPanel({ doc, onChanged, sector, title }) {
  const [date, setDate] = useState(doc.review_due || doc.suggested_review_due);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/me/policies/${sector}/${encodeURIComponent(title)}/review`, { review_due: date }); toast.success("Review recorded"); onChanged(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border p-4 space-y-2" data-testid="policy-review-panel">
      <p className="font-medium text-sm flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-accent" /> Review schedule</p>
      <p className="text-xs text-muted-foreground">Frequency: {doc.content.review_frequency || "Annually"}{doc.last_reviewed && ` · last reviewed ${new Date(doc.last_reviewed).toLocaleDateString("en-GB")}`}</p>
      <label className="block text-xs">Next review due
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="policy-review-date" />
      </label>
      <Button size="sm" onClick={save} disabled={busy || !date} data-testid="policy-mark-reviewed">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark reviewed today"}</Button>
    </div>
  );
}

function SignoffPanel({ doc, onChanged, sector, title }) {
  const [form, setForm] = useState({ name: "", role: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/me/policies/${sector}/${encodeURIComponent(title)}/signoff`, form); setForm({ name: "", role: "" }); onChanged(); toast.success("Sign-off recorded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not record"); } finally { setBusy(false); }
  };
  const remove = async (i) => { await api.delete(`/me/policies/${sector}/${encodeURIComponent(title)}/signoff/${i}`); onChanged(); };
  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="policy-signoff-panel">
      <p className="font-medium text-sm flex items-center gap-2"><UserCheck className="w-4 h-4 text-accent" /> Staff sign-off <Badge variant="secondary" data-testid="policy-signoff-count">{doc.signoffs.length}</Badge></p>
      {doc.signoffs.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="policy-signoff-list">
          {doc.signoffs.map((s, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span><b>{s.name}</b>{s.role && ` · ${s.role}`}{s.email && ` · ${s.email}`} · {new Date(s.signed_at).toLocaleDateString("en-GB")}</span>
              <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive" data-testid={`policy-signoff-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="space-y-2">
        <Input placeholder="Staff member name" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="policy-signoff-name" />
        <Input placeholder="Role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="policy-signoff-role" />
        <Button size="sm" type="submit" variant="outline" disabled={busy} data-testid="policy-signoff-add">Record sign-off</Button>
      </form>
    </div>
  );
}

export default function PolicyEditor() {
  const { sector, title: rawTitle } = useParams();
  const title = decodeURIComponent(rawTitle);
  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get(`/me/policies/${sector}/${encodeURIComponent(title)}`).then((r) => { setDoc(r.data); setContent(r.data.content); })
    .catch((e) => { logError("policy", e); setError(formatApiError(e.response?.data?.detail) || "Could not load policy"); });
  useEffect(() => { load(); }, [sector, title]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy("save");
    try { await api.put(`/me/policies/${sector}/${encodeURIComponent(title)}`, { content }); toast.success("Your version saved"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); } finally { setBusy(null); }
  };
  const reset = async () => {
    if (!window.confirm("Discard your edits and return to the master policy?")) return;
    setBusy("reset");
    try { await api.post(`/me/policies/${sector}/${encodeURIComponent(title)}/reset`); toast.success("Reset to master"); await load(); } finally { setBusy(null); }
  };
  const exportDoc = async (format) => {
    setBusy(format);
    try { const res = await api.get(`/me/policies/${sector}/${encodeURIComponent(title)}/export`, { params: { format }, responseType: "blob" }); downloadBlob(res); }
    catch { toast.error("Export failed"); } finally { setBusy(null); }
  };

  if (error) return <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="policy-editor-error"><p className="text-destructive mb-4">{error}</p><Button asChild variant="outline"><Link to="/policies">Back to My Policies</Link></Button></div>;
  if (!doc || !content) return <div className="max-w-6xl mx-auto px-4 py-14 text-sm text-muted-foreground">Loading policy…</div>;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="policy-editor">
      <Link to="/policies" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="policy-editor-back"><ArrowLeft className="w-4 h-4" /> My Policies</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="policy-editor-title">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{doc.org_name ? `Prepared for ${doc.org_name}` : "Add your organisation name in Dashboard settings to auto-fill it"}{doc.customised && <Badge variant="secondary" className="ml-2">Customised</Badge>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => exportDoc("docx")} data-testid="policy-export-docx"><Download className="w-4 h-4 mr-1" /> Word</Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => exportDoc("pdf")} data-testid="policy-export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          {doc.customised && <Button variant="ghost" size="sm" disabled={!!busy} onClick={reset} data-testid="policy-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset to master</Button>}
          <Button size="sm" disabled={!!busy} onClick={save} data-testid="policy-save">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save my version</>}</Button>
        </div>
      </div>
      {doc.master_updated_at && <MasterUpdateBanner doc={doc} sector={sector} title={title} onChanged={load} />}
      <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
        <div className="bg-card border border-border rounded-2xl p-5"><PolicySectionEditor content={content} onChange={setContent} idPrefix="policy" /></div>
        <aside className="space-y-4 lg:sticky lg:top-24">
          <ReviewPanel doc={doc} sector={sector} title={title} onChanged={(d) => setDoc({ ...doc, ...d })} />
          <SignoffPanel doc={doc} sector={sector} title={title} onChanged={load} />
          <SignoffRequests doc={doc} sector={sector} title={title} onChanged={load} />
          <PolicyHistory sector={sector} title={title} refreshKey={doc.updated_at || doc.signoffs.length} />
          <VersionHistory version={doc.version} changelog={doc.changelog} />
        </aside>
      </div>
    </div>
  );
}
